import { useMemo, useEffect } from 'react'
import { fetchCruiseBookingById, fetchCruiseDepartureById, fetchCruiseOffer } from '../../actions/CruiseActions'
import { shallowEqual } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { canAddInsuranceToOrder } from 'lib/order/orderUtils'

interface CruiseState {
  offers?: { [key: string]: App.CruiseOffer }
  bookings?: { [key: string]: App.CruiseBookingInfo }
  departures?: { [key: string]: App.CruiseDepartureView }
  canAddInsurance?: boolean
  insuranceProductName?: string
}

export default function useCruisePurchaseDetails(
  order: App.Order,
  bookingId: string,
  allowInvalidOffer?: boolean,
) {
  const dispatch = useAppDispatch()
  const offerId = order?.cruiseItems[0]?.cruiseOfferId
  const offer = useAppSelector<App.CruiseOffer | undefined>((state) => (
    offerId ? state.cruise.cruiseOffers[offerId] : undefined
  ), shallowEqual)

  const { offers, bookings, departures, insuranceProductName, canAddInsurance } = useAppSelector<CruiseState>((state) => ({
    offers: state.cruise.cruiseOffers,
    bookings: state.cruise.cruiseBookingInfo,
    departures: state.cruise.cruiseDeparture,
    insuranceProductName: state.geo.insuranceProductName,
    canAddInsurance: canAddInsuranceToOrder(order, state.geo.currentRegionCode, offer),
  }), shallowEqual)

  const cruiseItem = order.cruiseItems[0]

  const bookingInfo = useMemo(() => {
    if (bookings && bookingId) return bookings[bookingId]
  }, [bookings, bookingId])

  const departureInfo = useMemo(() => {
    if (departures &&
      bookingInfo?.departureId) return departures[bookingInfo.departureId]
  }, [departures, bookingInfo])

  const offerInfo = useMemo(() => {
    if (offers &&
      departureInfo?.offer.id) return offers[departureInfo.offer.id]
  }, [offers, departureInfo])

  const travelers = useMemo(() => {
    if (bookingInfo?.travelersInfo?.length > 0) {
      const numberOfAdults = bookingInfo.travelersInfo.filter((item) => item.type === 'ADT').length
      const numberOfChilds = bookingInfo.travelersInfo.filter((item) => item.type === 'CHD').length
      return { numberOfAdults, numberOfChilds }
    }
  }, [bookingInfo?.travelersInfo])

  const cabinInfo = useMemo(() => {
    if ((offerInfo?.ship.cabinCategories.length > 0) &&
      (bookingInfo?.cabinCode || bookingInfo?.categoryCode)) {
      return offerInfo.ship.cabinCategories.find(
        (item) => item.code === (bookingInfo.cabinCode || bookingInfo.categoryCode))
    }
  }, [offerInfo?.ship.cabinCategories, bookingInfo?.categoryCode, bookingInfo?.cabinCode])

  // TODO: we need to create a single endpoint in svc-cruise to get all this
  // information about the booking instead of the 3 different requests in FE

  useEffect(() => {
    if (bookingId) dispatch(fetchCruiseBookingById(bookingId))
  }, [bookingId, dispatch])

  useEffect(() => {
    if (bookingInfo?.departureId) {
      dispatch(fetchCruiseDepartureById(bookingInfo.departureId))
    }
  }, [bookingInfo?.departureId, dispatch])

  useEffect(() => {
    if (departureInfo?.offer.id) {
      dispatch(fetchCruiseOffer(departureInfo.offer.id, allowInvalidOffer))
    }
  }, [dispatch, departureInfo?.offer.id, allowInvalidOffer])

  return {
    image: offerInfo?.ship.images[0],
    name: offerInfo?.name,
    bookingNumber: bookingInfo?.confirmationNumber,
    cruiseItem,
    bookingInfo,
    departureInfo,
    offerInfo,
    travelers,
    cabinInfo,
    insuranceProductName,
    canAddInsurance,
  }
}
